/**
 * Common regex patterns used throughout the application
 */
export const REGEX_PATTERNS = {
    RUPEE_SYMBOL_WITH_SPACES: /₹[\s\u00A0\u202F\u2007\u200A]*/gu,
    /**
     * INR currency prefix with any following whitespace.
     * Used for normalizing Intl.NumberFormat INR output to `₹ <amount>`.
     */
    INR_CURRENCY_PREFIX: /^₹\s*/,
    /**
     * Email validation regex
     * Matches standard email format: user@domain.com
     */
    EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    EMAIL_COMPOSER_SEGMENT:
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,

    /**
     * Phone number validation regex
     * Matches international and local phone number formats
     * Supports: +91-1234567890, 1234567890, 123-456-7890, (123) 456-7890
     */
    PHONE: /^[+]?[1-9][\d]{0,15}$|^[+]?[1-9][\d]{0,15}[-\s]?[\d]{1,15}[-\s]?[\d]{1,15}$|^\(?[1-9][\d]{2}\)?[-\s]?[\d]{3}[-\s]?[\d]{4}$/,

    /**
     * Password strength regex
     * Requires: 8+ chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
     */
    PASSWORD_STRONG: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,

    /**
     * Character set regex for password generation
     * Matches all alphabets (a-z, A-Z), digits (0-9), and special characters (!@#$%^&*)
     */
    PASSWORD_CHAR_SET: /[a-zA-Z0-9!@#$%^&*]/g,

    /**
     * URL validation regex for HTML sanitization
     * Allows http, https, mailto, tel, callto, cid, xmpp protocols
     */
    HTML_SANITIZER_URL:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,

    /**
     * URL validation regex for email sanitization (more restrictive)
     * Allows only http, https, mailto, tel protocols
     */
    EMAIL_SANITIZER_URL: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,

    /**
     * Client name: letters, numbers, spaces, dots, dashes and ampersand
     */
    CLIENT_NAME: /^[-a-zA-Z0-9\s.&]+$/,
    /**
     * Shiksha funder client name: allows additional business punctuation
     * (e.g. apostrophe, forward slash and ampersand).
     */
    CLIENT_NAME_SHIKSHA_FUNDER: /^[-a-zA-Z0-9\s./'&]+$/,

    /**
     * User name fields (first/middle/last/title/designation): Unicode letters, space, dot, hyphen.
     */
    USER_NAME_PART: /^[\p{L} .-]*$/u,

    /**
     * Parent flow — student name: letters, spaces, dots, and hyphens only (no digits).
     */
    STUDENT_NAME: /^[\p{L} .-]+$/u,

    /**
     * Section: letters, numbers, spaces, and hyphens only.
     */
    USER_SECTION: /^[a-zA-Z0-9 -]*$/,

    /**
     * Enrollment number: letters and numbers only (no dot or other symbols).
     */
    USER_ENROLLMENT_NUMBER: /^[a-zA-Z0-9]*$/,

    /**
     * Roll number: digits 0–9 only.
     */
    USER_ROLL_NUMBER: /^\d*$/,

    /**
     * Contact book first/last name: letters, spaces, apostrophe, hyphen only (no digits or punctuation).
     * `*` allows empty while the user is editing; `required` still applies where needed.
     */
    CONTACT_BOOK_PERSON_NAME: /^[a-zA-Z\s'-]*$/,

    /**
     * Strip disallowed characters for contact person name typing (inverse of CONTACT_BOOK_PERSON_NAME).
     */
    CONTACT_BOOK_PERSON_NAME_DISALLOWED_CHARS_GLOBAL: /[^a-zA-Z\s'-]/g,

    /**
     * `KeyboardEvent.key` is exactly one ASCII digit (keydown filter for digit-only fields).
     */
    KEY_EVENT_SINGLE_ASCII_DIGIT: /^\d$/,

    /**
     * Website URL: must use https:// scheme, valid host (multi-label), optional port and path
     */
    // Website URL must be HTTPS and contain a "real" TLD segment (letters only, 2-6 chars).
    // Example valid: https://www.example.com
    // Example invalid (missing/incorrect TLD): https://www.dpsschool
    WEBSITE_URL: /^https:\/\/(?:[\w-]+\.)+[A-Za-z]{2,6}(?::\d{1,5})?(?:\/\S*)?$/i,

    /**
     * Leading http:// or https:// URL scheme (case-insensitive).
     */
    HTTP_OR_HTTPS_PREFIX: /^https?:\/\//i,

    /**
     * Website for national / school (non-Shiksha-funder) client creation: domain-style host required;
     * http:// or https:// optional. Shiksha funder types continue to use {@link WEBSITE_URL}.
     */
    WEBSITE_URL_OPTIONAL_SCHEME:
        /^(?:https?:\/\/)?(?:[\w-]+\.)+[A-Za-z]{2,63}(?::\d{1,5})?(?:\/\S*)?$/i,

    /**
     * National School (sales) client creation: must start with www., no http(s):// prefix;
     * hostname must end with .com, .edu, or .co.in only. Optional port and path.
     */
    WEBSITE_URL_NATIONAL_WWW:
        /^www\.(?:(?:[\w-]+\.)+(?:com|edu)|(?:[\w-]+\.)+co\.in)(?::\d{1,5})?(?:\/\S*)?$/i,

    /**
     * LinkedIn URL: must use https:// scheme; standard profile/company paths only
     */
    LINKEDIN_URL:
        /^https:\/\/(www\.)?linkedin\.com\/(in|pub|company|school)\/[\w.-]+\/?(?:\?\S*)?$/i,

    /**
     * 10-digit Indian mobile number (no country code)
     */
    INDIAN_MOBILE_10_DIGIT: /^\d{10}$/,

    /**
     * National sales / Indian mobile: 10 digits, first digit 6–9 (TRAI allocation)
     */
    NATIONAL_SALES_MOBILE_10_DIGIT: /^[6-9]\d{9}$/,

    INDIAN_COUNTRY_CODE_PREFIX: /^(\+91|91)/,

    /**
     * Stage pattern: matches "s" followed by digits (case-insensitive)
     * Example: "s0", "S1", "s12"
     */
    STAGE_PATTERN: /s(\d+)/i,

    /**
     * Deal filter / Shiksha stage option value: entire string is "s" + digits only (case-insensitive).
     * Example: "s1", "S4"
     */
    STAGE_OPTION_VALUE: /^s(\d+)$/i,

    /**
     * Year pattern: matches 4-digit year
     * Example: "2024", "2025"
     */
    YEAR_PATTERN: /(\d{4})/,

    /**
     * Academic year extraction: matches 4-digit year at the start of string
     * Example: "2024-2025" -> "2024", "2024" -> "2024"
     */
    ACADEMIC_YEAR_EXTRACT: /^(\d{4})/,

    /**
     * Academic year label anchored: YYYY or YYYY-YYYY (capture group 1 = start year)
     * Example: "2025", "2025-2026"
     */
    ACADEMIC_YEAR_LABEL_ANCHORED: /^(\d{4})-(\d{4})$/,

    /**
     * Lead conversion probability: first percent value in label (capture group 1 = integer %)
     * Example: "High (50%)", "50%", "75.5%"
     */
    LEAD_CONVERSION_PROBABILITY_PERCENT_CAPTURE: /(\d{1,3})(?:\.\d+)?\s*%?/,

    /**
     * Financial year pattern: matches format YYYY-YY
     * Example: "2024-25", "2025-26"
     */
    FINANCIAL_YEAR_PATTERN: /^(\d{4})-\d{2}$/,

    /**
     * Phone format pattern: 5 digits, space, 5 digits
     * Example: "12345 67890"
     */
    PHONE_FORMAT_5_5: /^\d{5}\s\d{5}$/,

    /**
     * Digits only pattern (for phone validation)
     */
    DIGITS_ONLY_PATTERN: /^\d*$/,

    /**
     * Single whitespace character (global match)
     * Used for removing all spaces from strings
     */
    SINGLE_SPACE_GLOBAL: /\s/g,

    /**
     * One or more whitespace characters (global match)
     * Used for removing all whitespace from strings
     */
    WHITESPACE_GLOBAL: /\s+/g,

    /**
     * One or more whitespace (split tokenization, e.g. title-case words).
     * Same pattern as WHITESPACE_GLOBAL; omit `g` when a non-global RegExp is preferred.
     */
    ONE_OR_MORE_WHITESPACE: /\s+/,
    NAME_DOT_GLOBAL: /[.]/g,
    NAME_NON_ALPHA_GLOBAL: /[^a-z]/g,
    /**
     * 6-digit Indian pincode
     */
    PINCODE_INDIA_6_DIGIT: /^\d{6}$/,

    /**
     * 6-digit Indian pincode within a longer address string (word boundary).
     */
    PINCODE_INDIA_6_DIGIT_WORD_BOUNDARY: /\b\d{6}\b/,

    /**
     * Comma-separated list of phone numbers (digits, spaces, commas)
     */
    MULTI_PHONE_LIST: /^[\d\s,]+$/,

    /**
     * India GSTIN - Updated regex for proper GST validation
     * Format: 2 digits (state) + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
     */
    GSTIN_INDIA: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/,

    /**
     * India TAN
     */
    TAN_INDIA: /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/,

    TIME_12HOUR: /^(0?[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM|am|pm)$/i,
    TIME_24HOUR: /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/,
    TIME_PARSE_WITH_OPTIONAL_PERIOD: /(\d{1,2}):(\d{2})\s*(AM|PM)?/i,
    /**
     * Strict HH:mm parse pattern (1–2 digit hour, 2-digit minute, fully anchored)
     * Used for validating and extracting hours/minutes from time input values
     */
    TIME_HH_MM: /^(\d{1,2}):(\d{2})$/,

    /**
     * India PAN (Permanent Account Number)
     * Format: AAAAA9999A (5 letters, 4 digits, 1 letter)
     */
    PAN_INDIA: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,

    /**
     * Digits only (one or more)
     */
    DIGITS_ONLY: /^\d+$/,

    /**
     * Sales Funnel stage code in the format "S<number>" (capturing stage id)
     * Example matches: "S1", "S12"
     */
    SALES_FUNNEL_STAGE_CODE: /^S(\d+)$/,

    /**
     * CRM lead stage label: "S" + digits, case-insensitive; capture group 1 = stage number.
     * Unanchored so it still matches when the value appears inside a longer display string.
     */
    CRM_LEAD_STAGE_NUMBER_CAPTURE: /S(\d+)/i,

    /**
     * Non-numeric characters (for removing from numeric inputs)
     */
    NON_NUMERIC: /[^0-9]/g,

    /**
     * Non-letter characters (Unicode \p{L}); strip for first/last name fields
     */
    NON_LETTER_CHARS_GLOBAL: /[^\p{L}]/gu,

    /**
     * Non-name-part characters for middle/last name fields.
     * Allowed: Unicode letters (\p{L}), space, dot (.), hyphen (-)
     */
    NON_NAME_PART_CHARS_GLOBAL: /[^\p{L} .-]/gu,

    /** Single-key validation for name parts (middle/last). */
    NAME_PART_ALLOWED_SINGLE_KEY: /^[\p{L} .-]$/u,

    /** Non-section characters; allowed: a-zA-Z0-9, space, and hyphen */
    NON_SECTION_CHARS_GLOBAL: /[^a-zA-Z0-9 -]/g,

    /**
     * Section “Other” keydown: a single printable key must match the section charset
     * (aligned with NON_SECTION_CHARS_GLOBAL stripping).
     */
    SECTION_OTHER_ALLOWED_SINGLE_KEY: /^[a-zA-Z0-9 -]$/,

    /** Non-alphanumeric characters for enrollment number (letters and digits only). */
    NON_ALPHANUMERIC_CHARS_GLOBAL: /[^a-zA-Z0-9]/g,

    /** Enrollment keydown: one character must be a letter or digit. */
    ALPHANUMERIC_SINGLE_KEY: /^[a-zA-Z0-9]$/,

    /**
     * Letters-only keydown (Unicode letters): one grapheme must be a letter.
     */
    LETTERS_ONLY_SINGLE_UNICODE_LETTER: /^\p{L}$/u,

    /**
     * ISO calendar date YYYY-MM-DD (groups: full, year, month, day).
     */
    ISO_DATE_YYYY_MM_DD: /^(\d{4})-(\d{2})-(\d{2})$/,

    /**
     * Non-digit characters (global). Strips formatting from phone-style strings; aligns with `\D`.
     */
    NON_DIGIT_GLOBAL: /\D/g,

    /**
     * Non-numeric and non-decimal characters (for numeric inputs with decimals)
     */
    NON_NUMERIC_DECIMAL: /[^0-9.]/g,

    DECIMAL_POINT_GLOBAL: /\./g,

    /**
     * Amount with up to 2 decimal places (e.g. 10, 10.5, 10.50)
     */
    AMOUNT_UPTO_TWO_DECIMALS: /^\d+(\.\d{0,2})?$/,

    /**
     * Allowed characters for pasted INR/currency input.
     * Supports digits, whitespace, comma separators, decimal point and rupee symbol.
     */
    CURRENCY_PASTE_ALLOWED: /^[\d\s,.\u20B9]+$/,

    /**
     * Comma thousands separators (e.g. INR display `11,222.11`) — remove before parseFloat
     */
    THOUSANDS_SEPARATOR_COMMA_GLOBAL: /,/g,

    /**
     * Non–a-z/0-9 run → single underscore (slug / snake_case normalization)
     */
    SNAKE_CASE_NON_ALPHANUMERIC_TO_UNDERSCORE: /[^a-z0-9]+/g,

    /**
     * Leading and trailing underscore runs (trim after snake_case step)
     */
    SNAKE_CASE_TRIM_UNDERSCORES: /^_+|_+$/g,

    /**
     * Positive integer as string, no leading zeros (e.g. student count "1"–"999…")
     */
    POSITIVE_INTEGER_STRING: /^[1-9]\d*$/,

    /**
     * Leading hyphen (for removing negative signs)
     */
    LEADING_HYPHEN: /^-/,

    /**
     * Number with optional decimals (capturing group)
     * Example matches: "3", "3.5", within larger strings
     */
    NUMBER_WITH_OPTIONAL_DECIMALS_CAPTURE: /(\d+(?:\.\d+)?)/,

    /**
     * First contiguous run of digits in a string (capturing group).
     * Used for sort keys and coarse numeric extraction from labels (e.g. timeframe text).
     */
    FIRST_INTEGER_CAPTURE: /(\d+)/,

    /**
     * Single uppercase letter (A-Z)
     */
    UPPERCASE_LETTER: /[A-Z]/,

    /**
     * Single digit (0-9)
     */
    SINGLE_DIGIT: /[0-9]/,

    /**
     * Entire string is one digit repeated (e.g. "1111111") — invalid vanity phone subscriber.
     */
    REPEATED_SINGLE_DIGIT_ONLY: /^(\d)\1+$/,

    /**
     * Default Z character for GST (14th position)
     */
    GST_DEFAULT_Z: /[Z]/,

    /**
     * Alphanumeric character (A-Z, 0-9)
     */
    ALPHANUMERIC: /[A-Z0-9]/,

    /**
     * Address line allowed characters (letters, numbers, spaces, dash, dot, comma)
     */
    ADDRESS_TEXT_ALLOWED: /^[a-zA-Z0-9\s.,-]+$/,

    /**
     * Address line: letters, numbers and spaces; must include at least one letter or digit (not whitespace-only).
     */
    ADDRESS_LINE1_ALPHANUMERIC_SPACE: /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s]+$/,

    /**
     * National Sales address: letters, numbers, spaces, and , . ' # / - (3–100 chars);
     * must include at least one letter or digit (not whitespace-only).
     */
    ADDRESS_NATIONAL_SALES: /^(?=.*[A-Za-z0-9])[A-Za-z0-9\s,.'#/-]{3,100}$/,

    /**
     * Reassignment API messages where a lead is already assigned to a user id
     */
    REASSIGN_ALREADY_ASSIGNED_TO_USER_ID_1: /already assigned to user[_ ]?id\s*[-:]?\s*([\w-]+)/i,
    REASSIGN_ALREADY_ASSIGNED_TO_USER_ID_2: /assigned to\s+([\w-]+)(?=[:\s]|$)/i,

    /**
     * Order ID specific regexes
     */
    ORDER_ID_NUMBER_ONLY: /^\d+$/,
    ORDER_ID_NON_NUMERIC: /[^0-9]/g,
    ORDER_ID_LEADING_ZEROS: /^0+/,
    ORDER_ID_VALID: /^[1-9]\d{0,7}$/,

    /**
     * Numeric input sanitization
     * Removes leading zeros only when the next character is a digit (keeps "0.5")
     */
    NUMERIC_LEADING_ZEROS_BEFORE_DIGIT: /^0+(?=\d)/,

    /**
     * Organization name sanitization regexes
     * NON_ALPHANUMERIC: Matches any character that is not alphanumeric (a-z, A-Z, 0-9)
     * MULTIPLE_UNDERSCORES: Matches one or more consecutive underscores
     */
    NON_ALPHANUMERIC: /[^a-zA-Z0-9]/g,
    MULTIPLE_UNDERSCORES: /_+/g,

    /**
     * Numeric extraction regex
     * Removes all characters except digits and decimal points
     * Used for extracting numeric values from formatted currency strings
     */
    NUMERIC_EXTRACT: /[^\d.]/g,

    /**
     * Matches one or more whitespace characters (spaces, tabs, newlines)
     */
    WHITESPACE: /\s+/g,

    /**
     * Trailing zeros in decimal numbers (with optional decimal point)
     * Used for trimming formatted percentage values
     */
    TRAILING_DECIMAL_ZEROS: /\.?0+$/,

    /**
     * Split placeholder into label text and trailing required-marker asterisks.
     * Group 1: text; group 2: one or more * at end (e.g. ei-dropdown placeholder).
     */
    PLACEHOLDER_TEXT_TRAILING_ASTERISKS: /^(.*?)(\*+)$/,

    SPACES_TO_UNDERSCORE: /\s+/g,
    UNDERSCORE_GLOBAL: /_/g,
    HYPHEN_WITH_SPACES: /\s*-\s*/g,
    /**
     * One or more whitespace or hyphen characters (global match).
     * Used for normalizing strings like "printing by school" -> "printing_by_school".
     */
    WHITESPACE_OR_HYPHEN_GLOBAL: /[\s-]+/g,
    MULTIPLE_UNDERSCORES_SEQUENCE: /__+/g,
    LEADING_TRAILING_UNDERSCORE: /^_|_$/g,

    STATUS_SEPARATORS: /[_-]+/g, // Replace common "word separators" with a single space (e.g., contract_signed, contract-signed)
    MULTI_SPACE: /\s+/g, // Collapse repeated whitespace into a single space

    /**
     * Deal name sanitization: matches specific special characters to remove
     * Removes: hyphen (-), underscore (_), comma (,), period (.), and brackets ((), [], {})
     */
    DEAL_NAME_SPECIAL_CHARS: /[-_,.()[\]{}]/g,

    /**
     * Country code prefix pattern (capturing group)
     * Matches country code at start of string with optional space: "+91 " or "+91"
     * Example: "+91 ", "+1 "
     */
    COUNTRY_CODE_PREFIX_CAPTURE: /^(\+\d+\s*)/,

    /**
     * Country code prefix pattern (for replacement)
     * Matches country code at start of string with optional space
     * Used to remove country code from phone numbers
     */
    COUNTRY_CODE_PREFIX: /^\+\d+\s*/,

    /**
     * Country code in string (capturing group)
     * Matches country code anywhere in string: "+91", "+1", etc.
     * Captures the digits after the plus sign
     */
    COUNTRY_CODE_IN_STRING: /\+(\d+)/,

    /**
     * Country code with phone number (capturing groups)
     * Matches full phone format: country code + optional space + phone number
     * Group 1: country code (e.g., "+91")
     * Group 2: phone number part
     */
    COUNTRY_CODE_WITH_PHONE: /^(\+\d+)\s*(.*)$/,

    /**
     * Digits only (global match)
     * Used for matching/removing digits from strings
     */
    DIGITS_GLOBAL: /\d/g,

    /**
     * Email domain extraction regex - extracts domain part from email
     */
    EMAIL_DOMAIN_EXTRACT: /^[^@]+@(.+)$/i,

    /**
     * Regex meta characters to escape when using a string as a literal in a regex
     */
    REGEX_META_ESCAPE: /[.*+?^${}()|[\]\\]/g,

    /**
     * Contract signature image data URI prefix (PNG/JPEG/JPG) with base64 payload.
     * Capturing group 1 contains the base64 value.
     */
    CONTRACT_SIGNATURE_DATA_URI_BASE64: /^data:image\/(?:png|jpeg|jpg);base64,(.+)$/i,

    /**
     * CSL order review: strips the display prefix from grade labels like "Grade 1".
     */
    CSL_GRADE_LABEL_PREFIX: /^grade\s*/i,

    /**
     * CSL order review contact comparison fields supported as KDM rows.
     */
    CSL_KDM_CONTACT_FIELD: /^(?<role>kdm)_(?<kind>name|email|phone)$/i,

    /**
     * Matches characters that are unsafe for file names.
     * Allowed characters are letters, numbers, underscore and hyphen.
     */
    FILE_NAME_UNSAFE_CHARS: /[^a-zA-Z0-9_-]/g,
    /**
     * Upload file name charset: letters, digits, dot, underscore, and hyphen only.
     */
    FILE_NAME_BULK_UPLOAD_ALLOWED: /^[A-Za-z0-9._-]+$/,

    /**
     * Secure string (aligned with extendedJoi.secureString()): letters (a-z, A-Z),
     * digits (0-9), and spaces only. Used for additional_notes in lead creation.
     */
    SECURE_STRING: /^[a-zA-Z0-9\s]*$/,

    /** Shiksha CRM virtual meeting — custom platform name (letters, digits, spaces). */
    SHIKSHA_VIRTUAL_MEETING_CUSTOM_PLATFORM_NAME: /^[a-zA-Z0-9 ]+$/,

    SHIKSHA_CLIENT_TYPE_AMOUNT_WITH_BRACKETS: /^(.*?)\s*\(([^)]+)\)\s*$/,

    COOKIE_VALUE: ((name: string) => new RegExp(`(?:^|; )${name}=([^;]*)`)) as (
        name: string,
    ) => RegExp,
} as const

/**
 * Common string replacement values used with regex patterns
 */
export const STRING_REPLACEMENTS = {
    UNDERSCORE: '_',

    /**
     * Currency formatting helpers
     * RUPEE_SYMBOL_WITH_SPACES: rupee symbol followed by whitespace or common thin spaces to normalize spacing
     */
} as const

/**
 * Character sets for password generation and other uses
 */
export const CHARACTER_SETS = {
    /**
     * Character set for secure password generation
     * Derived from PASSWORD_STRONG regex pattern character classes
     */
    PASSWORD_CHARS: (() => {
        // Extract characters from the regex pattern's character class [A-Za-z\d@$!%*?&]
        const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        const lowercase = 'abcdefghijklmnopqrstuvwxyz'
        const digits = '0123456789'
        const special = '@$!%*?&'
        return uppercase + lowercase + digits + special
    })(),
} as const

/**
 * Supported file extensions extracted from FILE_EXTENSION regex pattern
 */
export const FILE_EXTENSIONS = [
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xls',
    'xlsx',
    'txt',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'svg',
    'webp',
] as const

/**
 * Type for supported file extensions
 */
export type FileExtension = (typeof FILE_EXTENSIONS)[number]

/**
 * Type for regex pattern keys
 */
export type RegexPatternKey = keyof typeof REGEX_PATTERNS

export const SEAT_COUNT_REGEX = /^(\d+)/
export const GRADE_NUMBER_REGEX = /\d+/

/**
 * Known email domains for validation - popular email providers that users commonly mistype
 */
export const KNOWN_EMAIL_DOMAINS = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'icloud.com',
    'zoho.com',
    'ei.study',
] as const
